import { Button } from "@/components/ui/button"
import { FieldGroup } from "@/components/ui/field-group"
import { FormActions } from "@/components/ui/form-actions"
import { H1, H2 } from "@/components/ui/headings"
import { RhfField } from "@/components/ui/rhf-field"
import { useToast } from "@/components/ui/use-toast"
import { gql } from "@/generated"
import { UserRole } from "@/generated/graphql"
import { displayValidationErrors } from "@/util/validations"
import { useMutation } from "@apollo/client"
import { zodResolver } from "@hookform/resolvers/zod"
import React from "react"
import { FormProvider, useForm } from "react-hook-form"
import { z, ZodType } from "zod"

type FormValues = {
  email: string
}

export const ResetPasswordMutation = gql(`
  mutation ResetPassword($input: SendPasswordResetTokenInput!) {
    sendPasswordResetToken(input: $input) {
      errors {
        path
        fullMessages
      }
    }
  }
`)

const schema: ZodType<FormValues> = z.object({
  email: z
    .string()
    .min(1, "Email address is required")
    .email({ message: "Email address is invalid" }),
})

const ResetPasswordForm = ({
  setEmail,
}: {
  setEmail: (email: string) => void
}) => {
  const { toast } = useToast()

  const [mutate] = useMutation(ResetPasswordMutation)

  const defaultValues: FormValues = {
    email: "",
  }

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  })
  const { control, formState, setError } = methods

  const onSubmit = async (values: FormValues) => {
    try {
      const { data } = await mutate({
        variables: {
          input: { ...values, userRole: UserRole.OrgAdmin },
        },
      })

      if (
        data?.sendPasswordResetToken.errors &&
        data?.sendPasswordResetToken.errors.length > 0
      ) {
        throw data?.sendPasswordResetToken.errors
      }

      setEmail(values.email)
    } catch (e: any) {
      console.error(e)
      displayValidationErrors({
        recordType: "user",
        errors: e,
        setError,
        toast,
        validationErrorTitle: "Password reset failed, please try again",
      })
    }
  }

  return (
    <FormProvider {...methods}>
      <div className="mb-12">
        <H1>Forgot Your Password?</H1>
        <H2>We'll email you instructions for resetting your password.</H2>
      </div>

      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <FieldGroup>
          <RhfField
            name="email"
            control={control}
            placeholder="Email address"
            label="Email Address"
            autoFocus={true}
          />
        </FieldGroup>

        <FormActions>
          <Button
            type="submit"
            variant="default"
            disabled={formState.isSubmitting}
          >
            Send Recovery Email
          </Button>
        </FormActions>
      </form>
    </FormProvider>
  )
}

export default ResetPasswordForm
